import React from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Profile } from '@/hooks/useProfile';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw } from 'lucide-react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DISCIPLINES, EDUCATION_LEVELS, EXPERIENCE_LEVELS } from "@/data/constants";
import { ALL_COUNTRIES } from "@/data/countries";
import { getFlagEmoji } from '@/utils/countryFlags';
import { generateBio } from '@/utils/bioGenerator';
import { useBasicInfoForm } from './hooks/useBasicInfoForm';

interface BasicInfoFormProps {
  profile: Profile;
  setProfile: (field: keyof Profile | string, value: any) => void;
}

const BasicInfoForm = ({ profile, setProfile }: BasicInfoFormProps) => {
  const {
    experienceLevel,
    isGeneratingBio,
    setExperienceLevel,
    getYearsFromExperienceLevel,
    handleGenerateBio
  } = useBasicInfoForm(profile, setProfile);

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Basic Information</h3>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="username">Full Name</Label>
          <Input
            id="username"
            type="text"
            value={profile.username || ''}
            onChange={(e) => setProfile('username', e.target.value)}
            placeholder="Your full name"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="profession">Discipline</Label>
          <Select
            value={profile.profession || ''}
            onValueChange={(value) => setProfile('profession', value)}
          >
            <SelectTrigger id="profession" className="w-full">
              <SelectValue placeholder="Select your discipline" />
            </SelectTrigger>
            <SelectContent>
              <div className="max-h-[300px] overflow-y-auto">
                {DISCIPLINES.map((discipline) => (
                  <SelectItem key={discipline.id} value={discipline.label}>
                    {discipline.label}
                  </SelectItem>
                ))}
              </div>
            </SelectContent>
          </Select>
        </div>
        
        {/* PERSIST USERS THE LOCATION HERE*/}
        <div className="space-y-2">
          <Label htmlFor="location">Location</Label>
          <Select
            value={profile.location || ''}
            onValueChange={(value) => setProfile('location', value)}
          >
            <SelectTrigger id="location" className="w-full">
              <SelectValue placeholder="Select your country" />
            </SelectTrigger>
            <SelectContent>
              <div className="max-h-[300px] overflow-y-auto">
                {ALL_COUNTRIES.map((country) => {
                  const flag = getFlagEmoji(country.label) || getFlagEmoji(country.id);
                  return (
                    <SelectItem key={country.id} value={country.id}>
                      <span className="inline-flex items-center gap-2">
                        {flag && <span className="text-base leading-none">{flag}</span>}
                        <span>{country.label}</span>
                      </span>
                    </SelectItem>
                  );
                })}
              </div>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="education">Education</Label>
          <Select
            value={profile.education || ''}
            onValueChange={(value) => setProfile('education', value)}
          >
            <SelectTrigger id="education" className="w-full">
              <SelectValue placeholder="Select your highest education level" />
            </SelectTrigger>
            <SelectContent>
              <div className="max-h-[300px] overflow-y-auto">
                {EDUCATION_LEVELS.map((education) => (
                  <SelectItem key={education.id} value={education.id}>
                    {education.label}
                  </SelectItem>
                ))}
              </div>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="years_experience">Years of Experience</Label>
          <Select
            value={experienceLevel}
            onValueChange={setExperienceLevel}
          >
            <SelectTrigger id="years_experience" className="w-full">
              <SelectValue placeholder="Select your experience level" />
            </SelectTrigger>
            <SelectContent>
              <div className="max-h-[300px] overflow-y-auto">
                {EXPERIENCE_LEVELS.map((level) => (
                  <SelectItem key={level.id} value={level.id}>
                    {level.label}
                  </SelectItem>
                ))}
              </div>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 mt-6">
          <Label htmlFor="bio">Bio</Label>
          <div className="flex flex-col gap-2">
            <Textarea
              id="bio"
              value={profile.bio || ''}
              onChange={(e) => setProfile('bio', e.target.value)}
              placeholder="Tell us about yourself and your work"
              rows={4}
            />
            <Button 
              type="button" 
              variant="outline" 
              className="self-end flex items-center gap-2"
              onClick={handleGenerateBio}
              disabled={isGeneratingBio || !profile.username || !profile.profession}
            >
              {isGeneratingBio ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Generate Bio
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Fill in your details above to generate a personalized bio automatically
          </p>
        </div>
      </div>
    </div>
  );
};

export default BasicInfoForm;
